import { CorrectiveActionsRequest } from '~/correctiveActions/api/requests';
import type { UserStore } from '~/authentication/stores/user';
import type ApiClient from '~/common/services/ApiClient';
import type { User } from '~/users/types';
import type { Location } from '~/tracking/types';
import type { CustomChartData } from '~/common/types';
import moment, { Moment } from 'moment';
import { Card } from '../types';
import type { CorrectiveAction } from '~/correctiveActions/types';
import { ChartData } from 'chart.js';
import { Ticket } from '~/ticketWallet/types';
import { UsersRequest } from '~/users/api/requests';
import { TrackingsRequest } from '~/tracking/api/requests';
import { TopSubmittedFormsRequest } from '~/forms/api/requests';
import { SubmittedForm } from '~/forms/types';
import { colors } from '~/common/components/Charts/customChartjs';

export class SummaryDashboardService {
  apiClient: ApiClient;
  userStore: UserStore;

  constructor(apiClient: ApiClient, userStore: UserStore) {
    this.apiClient = apiClient;
    this.userStore = userStore;
  }

  async usage(params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new UsersRequest({
      token: accessToken,
      params: { ...params },
    });

    const response = await this.apiClient.request(request);
    return this.transformUsage(response?.data);
  }

  async topSubmittedForms(params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new TopSubmittedFormsRequest({
      token: accessToken,
      params: { ...params },
    });

    const response = await this.apiClient.request(request);
    return this.transformForms(response?.data);
  }

  async alertsCheckIns(params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new TrackingsRequest({
      token: accessToken,
      params: { ...params },
    });

    const response = await this.apiClient.request(request);
    return this.transformAlertCheckIns(response?.data);
  }

  async correctiveActions(params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new CorrectiveActionsRequest({
      token: accessToken,
      params: params,
    });

    const response = await this.apiClient.request(request);
    return this.transformCorrectiveActions(response?.data);
  }

  transformUsage(users: User[]) {
    const card: Card = {
      usersCount: 0,
      loading: false,
      expiresTickets60Days: [],
      expiredTickets: [],
      activeUsers: 0,
      activeForms: 0,
      inactiveUsers: [],
      inactiveUsersForms: [],
    };

    const groupsList: Record<string, any> = {};
    const groupsActivity: ChartData = {
      labels: [],
      datasets: [
        {
          backgroundColor: [
            colors.blue,
            colors.yellow,
            colors.teal,
            colors.purple,
            colors.orange,
          ],
          data: [],
        },
      ],
    };

    card.usersCount = users?.length;
    users?.map((user: User): any => {
      if (user.locations?.data.length) {
        card.activeUsers = card.activeUsers + 1;
      } else {
        card.inactiveUsers.push({
          label: user.fullName,
        });
      }

      if (user.submittedForms?.data.length) {
        card.activeForms = card.activeForms + 1;
      } else {
        card.inactiveUsersForms.push({
          label: user.fullName,
        });
      }

      if (user.locations?.data.length || user.submittedForms?.data.length) {
        this.addGroupsActivity(user, groupsList);
      }

      user.expiredIn60DaysTickets?.data.forEach((ticket: Ticket) => {
        card.expiresTickets60Days.push({
          label: user.fullName,
          value: ticket.ticketTitle,
        });
      });

      user.expiredTickets?.data.forEach((ticket: Ticket) => {
        card.expiredTickets.push({
          label: user.fullName,
          value: ticket.ticketTitle,
        });
      });
    });

    this.formatGroupsData(groupsList, groupsActivity);

    return {
      card: card,
      groupsActivity: groupsActivity,
    };
  }

  transformForms(submittedForms: SubmittedForm[]) {
    const forms: { [key: string]: CustomChartData } = {};
    submittedForms?.map((record: any) => {
      if (!forms[record.formID]) {
        forms[record.formID] = {
          x: 0,
          y: '',
          meta: [],
        };
      }

      forms[record.formID].meta.push({
        name: record.user?.data.fullName as string,
        total: record.cnt as number,
      });
      forms[record.formID].x += record.cnt;
      forms[record.formID].y = record.form?.data.title.split('_').join(' ');
    });

    const data: ChartData<'bar', CustomChartData[]> = {
      datasets: [
        {
          backgroundColor: 'rgb(54, 162, 235)',
          data: Object.values(forms),
        },
      ],
    };
    return data;
  }

  transformAlertCheckIns(response: Location[]) {
    const alertsCheckIns: { [key: string]: CustomChartData } = {};
    let startRedTime: Moment | null;
    const redTime = {};
    let countReds = 0;
    const alerts = {};
    const missedCheckIns = {};

    alertsCheckIns['yellow'] = {
      x: 0,
      y: 'Missed Check-Ins',
      meta: [],
    };

    alertsCheckIns['red'] = {
      x: 0,
      y: 'SOS Alerts',
      meta: [],
    };

    response?.forEach((record: Location) => {
      if (record.status === 'RED') {
        startRedTime = moment(new Date(record.created_at));
        if (!redTime[record.userID]) {
          redTime[record.userID] = {
            seconds: 0,
            user: null,
          };
        }
      } else if (startRedTime) {
        const endRedTime = moment(new Date(record.created_at));
        const seconds = moment
          .duration(endRedTime.diff(startRedTime))
          .asSeconds();

        if (redTime[record.userID]) {
          redTime[record.userID].seconds += seconds;
          redTime[record.userID].user = record.user?.data;
        }

        startRedTime = null;
        countReds++;
      }

      if (record.notes?.includes('USER HAS GONE RED')) {
        (alertsCheckIns['red'].x as number)++;
        if (!alerts[record.userID]) {
          alerts[record.userID] = {
            name: record.user?.data.fullName,
            total: 1,
          };
        } else {
          alerts[record.userID].total++;
        }
      } else if (record.notes?.includes('user missed their check-in')) {
        (alertsCheckIns['yellow'].x as number)++;
        if (!missedCheckIns[record.userID]) {
          missedCheckIns[record.userID] = {
            name: record.user?.data.fullName,
            total: 1,
          };
        } else {
          missedCheckIns[record.userID].total++;
        }
      }
    });

    var seconds = 0;
    for (const [key] of Object.entries(redTime)) {
      seconds = seconds + (redTime[key] as any).seconds;
    }

    if (seconds > 0) seconds = seconds / countReds;

    const timeInRed = moment.utc(seconds * 1000).format('HH:mm:ss');

    alertsCheckIns['red'].meta = Object.values(alerts);
    alertsCheckIns['yellow'].meta = Object.values(missedCheckIns);

    const alertCheckInsData =
      (alertsCheckIns['yellow'].x as number) +
        (alertsCheckIns['red'].x as number) >
      0
        ? [alertsCheckIns['yellow'], alertsCheckIns['red']]
        : [];

    const data: ChartData<'bar', CustomChartData[]> = {
      datasets: [
        {
          backgroundColor: [colors.yellow, colors.red],
          data: alertCheckInsData,
        },
      ],
    };
    return { data, timeInRed };
  }

  transformCorrectiveActions(actions: CorrectiveAction[]) {
    const today = moment();
    const chartData: any = {
      labels: ['Resolved', 'Open', 'Overdue'],
      datasets: [
        {
          backgroundColor: ['#64B046', '#E6BD21', '#C4272C'],
          data: [],
        },
      ],
    };

    const categories: Record<string, any> = {
      Resolved: { value: 0, meta: {} },
      Open: { value: 0, meta: {} },
      Overdue: { value: 0, meta: {} },
    };

    actions.forEach((action) => {
      const { resolved, targetDate, assignedUsers } = action;
      let date: Moment | null = null;
      if (targetDate) {
        date = moment(targetDate, 'YYYY-MM-DD HH:mm:ss');
      }

      let category: string;
      category =
        resolved === 1
          ? 'Resolved'
          : date === null || date.isSameOrAfter(today)
            ? 'Open'
            : 'Overdue';

      categories[category].value += 1;

      if (assignedUsers?.data?.length) {
        assignedUsers.data.forEach((user: User) => {
          const name = user.fullName;
          if (!categories[category].meta[name]) {
            categories[category].meta[name] = 0;
          }
          categories[category].meta[name] += 1;
        });
      } else {
        categories[category].meta['Unassigned'] = categories[category].meta[
          'Unassigned'
        ]
          ? categories[category].meta['Unassigned'] + 1
          : 1;
      }
    });

    const data = Object.keys(categories).map((key) => {
      const meta = Object.keys(categories[key].meta).map((name) => ({
        name,
        total: categories[key].meta[name],
      }));
      return {
        id: key,
        value: categories[key].value,
        meta,
      };
    });

    chartData.datasets[0].data = data;

    const total = chartData.datasets[0].data.reduce(
      (acc: number, curr: any) => acc + curr.value,
      0,
    );

    return { chartData: chartData, total: total.toString() };
  }

  addGroupsActivity(user: User, groupsList: Record<string, any>) {
    const activities = Array.from(
      new Set(user.locations?.data.map((location) => location.source)),
    );

    const countForms = user.submittedForms?.data.length ?? 0;
    const total = activities.length + countForms;

    user.groups?.data.forEach((group) => {
      const key = group.groupID;
      if (!group.allUsers && key) {
        if (!groupsList[key]) {
          groupsList[key] = {
            name: group.name,
            totalActivities: 0,
            details: {
              'LW/JM': 0,
              Forms: 0,
            },
          };
        }
        groupsList[key].totalActivities += total;
        groupsList[key].details['LW/JM'] += activities.length;
        groupsList[key].details['Forms'] += countForms;
      }
    });
  }

  formatGroupsData(
    groupsList: Record<string, any>,
    groupsActivity: Record<string, any>,
  ) {
    Object.keys(groupsList).forEach((groupId) => {
      const details = groupsList[groupId].details;
      Object.keys(details).forEach((key) => {
        if (details[key] === 0) {
          delete details[key];
        }
      });
    });

    let entries = Object.entries(groupsList);

    if (entries.length > 5) {
      entries = entries
        .sort((a, b) => b[1].totalActivities - a[1].totalActivities)
        .slice(0, 5);
    }

    entries.forEach((group) => {
      groupsActivity.labels.push(group[1].name);
      const metaData = Object.entries(group[1].details).map((entry) => {
        return { name: entry[0], total: entry[1] };
      });
      groupsActivity.datasets[0].data.push({
        id: group[0],
        value: group[1].totalActivities,
        meta: metaData,
      });
    });
  }
}
